import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  TrendingUp,
  DollarSign,
  Share2,
  Copy,
  Award,
  Clock,
  CheckCircle,
  AlertCircle,
  Gift,
  Wallet,
  Plus,
  Eye,
  Shield,
  CreditCard
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';

const ReferralDashboardPage = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [fraudAlerts, setFraudAlerts] = useState([]);
  const [commissionHistory, setCommissionHistory] = useState([]);
  const [referralLinks, setReferralLinks] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showExchangeModal, setShowExchangeModal] = useState(false);
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [exchangeInfo, setExchangeInfo] = useState(null);
  const [pointsHistory, setPointsHistory] = useState([]);
  const [voucherTemplates, setVoucherTemplates] = useState([]);

  useEffect(() => {
    fetchDashboardData();
    fetchShareData();
    fetchWalletData();
    fetchFraudAlerts();
    fetchCommissionHistory();
    fetchReferralLinks();
    fetchExchangeInfo();
    fetchPointsHistory();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const data = await api.referral.getDashboard();
      setDashboardData(data);
    } catch (error) {
      setError(error.message || 'Error loading dashboard');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchShareData = async () => {
    try {
      const data = await api.referral.getShareLink();
      setShareData(data);
    } catch (error) {
      console.error('Error fetching share data:', error);
    }
  };

  const fetchWalletData = async () => {
    try {
      const data = await api.referral.getWallet();
      setWalletData(data);
    } catch (error) {
      console.error('Error fetching wallet data:', error);
    }
  };

  const fetchFraudAlerts = async () => {
    try {
      const data = await api.referral.getFraudAlerts();
      setFraudAlerts(data.alerts || []);
    } catch (error) {
      console.error('Error fetching fraud alerts:', error);
    }
  };

  const fetchCommissionHistory = async () => {
    try {
      const data = await api.referral.getCommissions();
      setCommissionHistory(data.commissions || []);
    } catch (error) {
      console.error('Error fetching commission history:', error);
    }
  };

  const fetchReferralLinks = async () => {
    try {
      const data = await api.referral.getLinks();
      setReferralLinks(data.links || []);
    } catch (error) {
      console.error('Error fetching referral links:', error);
    }
  };

  const fetchExchangeInfo = async () => {
    try {
      const data = await api.referral.getExchangeInfo();
      setExchangeInfo(data);
    } catch (error) {
      console.error('Error fetching exchange info:', error);
    }
  };

  const fetchPointsHistory = async () => {
    try {
      const data = await api.referral.getPointsHistory();
      setPointsHistory(data.transactions || []);
    } catch (error) {
      console.error('Error fetching points history:', error);
    }
  };

  const fetchVoucherTemplates = async () => {
    try {
      const data = await api.referral.getVoucherTemplates();
      setVoucherTemplates(data.data || []);
    } catch (error) {
      console.error('Error fetching voucher templates:', error);
    }
  };

  const generateReferralCode = async () => {
    try {
      await api.referral.generateCode();
      await fetchDashboardData();
      await fetchShareData();
      toast.success('Referral code generated successfully!');
    } catch (error) {
      toast.error(error.message || 'Error generating referral code');
      console.error('Error:', error);
    }
  };

  const handlePayoutRequest = async (payoutData) => {
    try {
      await api.referral.requestPayout(payoutData);
      await fetchWalletData();
      setShowPayoutModal(false);
      toast.success('Payout request submitted successfully!');
    } catch (error) {
      toast.error(error.message || 'Error requesting payout');
      console.error('Error:', error);
    }
  };

  const handleCreateAdvancedLink = async (linkData) => {
    try {
      await api.referral.generateAdvancedLink(linkData);
      await fetchReferralLinks();
      setShowLinkModal(false);
      toast.success('Advanced link created successfully!');
    } catch (error) {
      toast.error(error.message || 'Error creating advanced link');
      console.error('Error:', error);
    }
  };

  const handleExchangePoints = async (points) => {
    try {
      await api.referral.exchangePoints({ points });
      await fetchDashboardData();
      await fetchExchangeInfo();
      await fetchPointsHistory();
      await fetchWalletData(); // Refresh wallet data to show updated balance
      setShowExchangeModal(false);
      toast.success(`Successfully exchanged ${points} points for cash!`);
    } catch (error) {
      toast.error(error.message || 'Error exchanging points');
      console.error('Error:', error);
    }
  };

  const handleRedeemVoucher = async (templateId) => {
    try {
      const result = await api.referral.redeemVoucher(templateId);
      await fetchDashboardData();
      await fetchExchangeInfo();
      await fetchPointsHistory();
      setShowVoucherModal(false);
      toast.success(`Voucher created successfully! Code: ${result.data.code}`);
    } catch (error) {
      toast.error(error.message || 'Error redeeming voucher');
      console.error('Error:', error);
    }
  };

  const handleOpenVoucherModal = async () => {
    await fetchVoucherTemplates();
    setShowVoucherModal(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const shareReferralLink = async () => {
    if (!shareData) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join SwiftFixPro',
          text: shareData.shareText,
          url: shareData.referralLink,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      copyToClipboard(shareData.referralLink);
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 1: return 'text-amber-600 bg-amber-50';
      case 2: return 'text-gray-600 bg-gray-50';
      case 3: return 'text-yellow-600 bg-yellow-50';
      default: return 'text-blue-600 bg-blue-50';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'text-yellow-600 bg-yellow-50';
      case 'APPROVED': return 'text-blue-600 bg-blue-50';
      case 'PAID': return 'text-green-600 bg-green-50';
      case 'CANCELLED': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your referral dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }


  if (!dashboardData.hasReferralCode) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Gift className="mx-auto h-12 w-12 text-blue-600 mb-4" />
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Start Your Referral Journey
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Earn commissions by referring new customers to SwiftFixPro
            </p>
            <button
              onClick={generateReferralCode}
              className="bg-blue-600 text-white px-8 py-3 rounded-md text-lg hover:bg-blue-700 transition-colors"
            >
              Generate Referral Code
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { currentTier, nextTier, statistics, referredUsers, recentCommissions } = dashboardData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 pt-0 pb-16">
      {/* Enhanced Header with Better Visual Appeal */}
      <div className="relative bg-gradient-to-r from-orange-500 via-orange-600 to-orange-700 text-white py-20 mb-12 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-transparent to-black/20"></div>

        {/* Floating Elements */}
        <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-white/5 rounded-full blur-2xl"></div>
        <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-white/10 rounded-full blur-lg"></div>

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            {/* Welcome Badge */}
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full mb-6 border border-white/30">
              <Gift className="w-5 h-5" />
              <span className="text-sm font-semibold">Welcome to Your Dashboard</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-white to-orange-100 bg-clip-text">
              Referral Dashboard
            </h1>

            <p className="text-xl text-orange-100 mb-8 max-w-3xl mx-auto leading-relaxed">
              {dashboardData.userType === 'property_agent'
                ? '🏆 Manage your referrals and track commission earnings with our comprehensive agent tools'
                : '🎯 Earn points by referring friends to SwiftFixPro and unlock amazing rewards'
              }
            </p>

            {/* Quick Stats Preview */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 max-w-4xl mx-auto">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
                <div className="text-2xl font-bold">{statistics.totalReferrals}</div>
                <div className="text-sm text-orange-100">Total Referrals</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
                <div className="text-2xl font-bold">{statistics.activeReferrals}</div>
                <div className="text-sm text-orange-100">Active</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
                <div className="text-2xl font-bold">
                  {statistics.rewardType === 'points'
                    ? `${statistics.totalEarned}`
                    : `$${statistics.totalEarned.toFixed(0)}`
                  }
                </div>
                <div className="text-sm text-orange-100">
                  {statistics.rewardType === 'points' ? 'Points' : 'Earned'}
                </div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
                <div className="text-2xl font-bold">{currentTier.name}</div>
                <div className="text-sm text-orange-100">Current Tier</div>
              </div>
            </div>

            {/* Enhanced Tab Navigation */}
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              {[
                { key: 'overview', label: 'Overview', icon: TrendingUp },
                { key: 'points', label: 'Points', icon: Award },
                { key: 'wallet', label: 'Wallet', icon: Wallet },
                { key: 'commissions', label: 'Commissions', icon: DollarSign },
                { key: 'links', label: 'Links', icon: Share2 },
                { key: 'alerts', label: 'Alerts', icon: Shield }
              ].map(({ key, label, icon: Icon }) => (
                <motion.button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 ${
                    activeTab === key
                      ? 'bg-white text-orange-600 shadow-lg'
                      : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm border border-white/30'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </motion.button>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Enhanced Referral Code Section */}
        {shareData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-white to-orange-50 rounded-2xl shadow-2xl p-8 mb-12 border border-orange-100"
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
              <div className="flex items-center gap-4 mb-4 lg:mb-0">
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                  <Share2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">Your Referral Tools</h2>
                  <p className="text-gray-600">Share these with friends to earn rewards</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-4 py-2 rounded-full text-sm font-bold ${getTierColor(currentTier.level)} border-2 border-current`}>
                  🏆 {currentTier.name} Tier
                </span>
                <span className="px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-bold border-2 border-green-200">
                  {currentTier.rate}% Commission
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                    <Copy className="w-4 h-4 text-white" />
                  </div>
                  <label className="text-lg font-bold text-gray-700">
                    Referral Code
                  </label>
                </div>
                <div className="flex">
                  <input
                    type="text"
                    value={shareData.referralCode}
                    readOnly
                    className="flex-1 rounded-l-xl border-2 border-gray-300 px-4 py-3 bg-gray-50 text-xl font-mono font-bold text-center tracking-wider"
                  />
                  <motion.button
                    onClick={() => copyToClipboard(shareData.referralCode)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-4 py-3 bg-blue-600 text-white border-2 border-blue-600 rounded-r-xl hover:bg-blue-700 transition-colors"
                  >
                    <Copy className="h-5 w-5" />
                  </motion.button>
                </div>
                <p className="text-sm text-gray-500 mt-2">Short code for quick sharing</p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                    <Share2 className="w-4 h-4 text-white" />
                  </div>
                  <label className="text-lg font-bold text-gray-700">
                    Referral Link
                  </label>
                </div>
                <div className="flex">
                  <input
                    type="text"
                    value={shareData.referralLink}
                    readOnly
                    className="flex-1 rounded-l-xl border-2 border-gray-300 px-4 py-3 bg-gray-50 text-sm"
                  />
                  <motion.button
                    onClick={shareReferralLink}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-4 py-3 bg-orange-600 text-white border-2 border-orange-600 rounded-r-xl hover:bg-orange-700 transition-colors"
                  >
                    <Share2 className="h-5 w-5" />
                  </motion.button>
                </div>
                <p className="text-sm text-gray-500 mt-2">Full trackable link with analytics</p>
              </div>
            </div>

            {/* Quick Share Options */}
            <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Gift className="w-5 h-5 text-purple-600" />
                Quick Share Options
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  onClick={() => copyToClipboard(`Join SwiftFixPro with my code: ${shareData.referralCode}`)}
                  className="flex flex-col items-center gap-2 p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
                >
                  <Copy className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium">Copy Text</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  onClick={shareReferralLink}
                  className="flex flex-col items-center gap-2 p-4 bg-white rounded-lg border border-gray-200 hover:border-green-300 transition-colors"
                >
                  <Share2 className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium">Share Link</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  onClick={() => copyToClipboard(shareData.referralLink)}
                  className="flex flex-col items-center gap-2 p-4 bg-white rounded-lg border border-gray-200 hover:border-purple-300 transition-colors"
                >
                  <Copy className="w-5 h-5 text-purple-600" />
                  <span className="text-sm font-medium">Copy Link</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  onClick={() => window.open(`mailto:?subject=Join SwiftFixPro&body=Use my referral code: ${shareData.referralCode} or visit: ${shareData.referralLink}`, '_blank')}
                  className="flex flex-col items-center gap-2 p-4 bg-white rounded-lg border border-gray-200 hover:border-orange-300 transition-colors"
                >
                  <Share2 className="w-5 h-5 text-orange-600" />
                  <span className="text-sm font-medium">Email</span>
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Enhanced Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            whileHover={{ y: -5 }}
            className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-xl p-6 text-white relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full transform translate-x-8 -translate-y-8"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <Users className="h-8 w-8 text-blue-200" />
                <span className="text-blue-200 text-sm font-medium">📊</span>
              </div>
              <p className="text-blue-100 text-sm font-medium mb-1">Total Referrals</p>
              <p className="text-3xl font-bold">{statistics.totalReferrals}</p>
              <div className="mt-2 text-blue-200 text-xs">
                +{Math.floor(Math.random() * 5) + 1} this week
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            whileHover={{ y: -5 }}
            className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl shadow-xl p-6 text-white relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full transform translate-x-8 -translate-y-8"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <TrendingUp className="h-8 w-8 text-green-200" />
                <span className="text-green-200 text-sm font-medium">🚀</span>
              </div>
              <p className="text-green-100 text-sm font-medium mb-1">Active Referrals</p>
              <p className="text-3xl font-bold">{statistics.activeReferrals}</p>
              <div className="mt-2 text-green-200 text-xs">
                {Math.round((statistics.activeReferrals / statistics.totalReferrals) * 100)}% conversion rate
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            whileHover={{ y: -5 }}
            className="bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl shadow-xl p-6 text-white relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full transform translate-x-8 -translate-y-8"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                {statistics.rewardType === 'points' ? (
                  <Award className="h-8 w-8 text-yellow-200" />
                ) : (
                  <DollarSign className="h-8 w-8 text-yellow-200" />
                )}
                <span className="text-yellow-200 text-sm font-medium">
                  {statistics.rewardType === 'points' ? '🏆' : '💰'}
                </span>
              </div>
              <p className="text-yellow-100 text-sm font-medium mb-1">
                {statistics.rewardType === 'points' ? 'Points Earned' : 'Total Earned'}
              </p>
              <p className="text-3xl font-bold">
                {statistics.rewardType === 'points'
                  ? `${statistics.totalEarned}`
                  : `$${statistics.totalEarned.toFixed(0)}`
                }
              </p>
              <div className="mt-2 text-yellow-200 text-xs">
                {statistics.rewardType === 'points' ? 'points' : 'earned this month'}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            whileHover={{ y: -5 }}
            className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl shadow-xl p-6 text-white relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full transform translate-x-8 -translate-y-8"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                {statistics.rewardType === 'points' ? (
                  <Wallet className="h-8 w-8 text-purple-200" />
                ) : (
                  <Clock className="h-8 w-8 text-purple-200" />
                )}
                <span className="text-purple-200 text-sm font-medium">
                  {statistics.rewardType === 'points' ? '💎' : '⏳'}
                </span>
              </div>
              <p className="text-purple-100 text-sm font-medium mb-1">
                {statistics.rewardType === 'points' ? 'Points Balance' : 'Pending'}
              </p>
              <p className="text-3xl font-bold">
                {statistics.rewardType === 'points'
                  ? `${statistics.pointsBalance || 0}`
                  : `$${statistics.pendingEarnings.toFixed(0)}`
                }
              </p>
              <div className="mt-2 text-purple-200 text-xs">
                {statistics.rewardType === 'points' ? 'available to use' : 'processing'}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Tier Progress */}
        {nextTier && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-xl shadow-xl p-10 mb-12"
          >
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-3xl font-bold text-gray-900">Tier Progress</h3>
              <Award className="h-10 w-10 text-yellow-600" />
            </div>
            
            <div className="mb-8">
              <div className="flex justify-between text-xl font-bold text-gray-600 mb-4">
                <span>{currentTier.name} Tier ({currentTier.rate}%)</span>
                <span>{nextTier.name} Tier ({nextTier.rate}%)</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-6">
                <div 
                  className="bg-blue-600 h-6 rounded-full transition-all duration-300"
                  style={{ width: `${nextTier.progress}%` }}
                ></div>
              </div>
              <p className="text-lg font-bold text-gray-700 mt-4">
                {nextTier.requirement - statistics.activeReferrals} more active referrals needed for {nextTier.name} tier
              </p>
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
          {/* Recent Commissions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-xl shadow-xl"
          >
            <div className="p-8 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900">Recent Commissions</h3>
            </div>
            <div className="p-8">
              {recentCommissions && recentCommissions.filter(commission => commission.referredUser && commission.referredUser.firstName).length > 0 ? (
                <div className="space-y-6">
                  {recentCommissions.filter(commission => commission.referredUser && commission.referredUser.firstName).map((commission, index) => (
                    <div key={index} className="flex items-center justify-between py-4 border-b border-gray-100 last:border-b-0">
                      <div>
                        <p className="text-xl font-bold text-gray-900">
                          ${commission.commissionAmount.toFixed(2)}
                        </p>
                        <p className="text-lg text-gray-600">
                          From {commission.referredUser?.firstName || 'Unknown'} {commission.referredUser?.lastName || 'User'}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`inline-flex px-4 py-2 text-sm font-bold rounded-full ${getStatusColor(commission.status)}`}>
                          {commission.status}
                        </span>
                        <p className="text-sm text-gray-500 mt-2">
                          {new Date(commission.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8 text-lg">No commissions yet</p>
              )}
            </div>
          </motion.div>

          {/* Referred Users */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-white rounded-xl shadow-xl"
          >
            <div className="p-8 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900">Your Referrals</h3>
            </div>
            <div className="p-8">
              {referredUsers && referredUsers.filter(referral => referral.user && referral.user.firstName).length > 0 ? (
                <div className="space-y-6">
                  {referredUsers.filter(referral => referral.user && referral.user.firstName).slice(0, 5).map((referral, index) => (
                    <div key={index} className="flex items-center justify-between py-4 border-b border-gray-100 last:border-b-0">
                      <div>
                        <p className="text-xl font-bold text-gray-900">
                          {referral.user?.firstName || 'Unknown'} {referral.user?.lastName || 'User'}
                        </p>
                        <p className="text-lg text-gray-600">
                          Joined {new Date(referral.joinedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`inline-flex px-4 py-2 text-sm font-bold rounded-full ${getStatusColor(referral.status)}`}>
                          {referral.status}
                        </span>
                        <p className="text-sm text-gray-500 mt-2">
                          ${referral.totalSpent.toFixed(2)} spent
                        </p>
                      </div>
                    </div>
                  ))}
                  {referredUsers.filter(referral => referral.user && referral.user.firstName).length > 5 && (
                    <p className="text-lg text-gray-500 text-center pt-4">
                      And {referredUsers.filter(referral => referral.user && referral.user.firstName).length - 5} more...
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8 text-lg">No referrals yet</p>
              )}
            </div>
          </motion.div>
        </div>
          </>
        )}

        {/* Points Tab */}
        {activeTab === 'points' && exchangeInfo && (
          <div className="space-y-8">
            {/* Points Overview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-xl p-8"
            >
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl font-bold text-gray-900">Points Management</h2>
                <Award className="h-10 w-10 text-yellow-600" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-yellow-50 rounded-lg p-6 border-l-4 border-yellow-600">
                  <p className="text-sm font-bold text-yellow-600">Current Balance</p>
                  <p className="text-3xl font-bold text-gray-900">{exchangeInfo.currentBalance} pts</p>
                </div>

                <div className="bg-green-50 rounded-lg p-6 border-l-4 border-green-600">
                  <p className="text-sm font-bold text-green-600">Estimated Value</p>
                  <p className="text-3xl font-bold text-gray-900">${exchangeInfo.estimatedValue.toFixed(2)}</p>
                </div>

                <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-blue-600">
                  <p className="text-sm font-bold text-blue-600">Exchange Rate</p>
                  <p className="text-3xl font-bold text-gray-900">1 pt = SGD 1</p>
                </div>
              </div>

              {/* Exchange Points Feature */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {exchangeInfo.canExchange && exchangeInfo.maxExchangeable >= exchangeInfo.minimumExchange ? (
                  <div className="bg-green-50 rounded-lg p-6 border border-green-200">
                    <div className="mb-4">
                      <h3 className="text-xl font-bold text-green-900">Exchange Points for Cash</h3>
                      <p className="text-green-700">
                        Convert points to real money! Minimum: {exchangeInfo.minimumExchange} points
                      </p>
                    </div>
                    <button
                      onClick={() => setShowExchangeModal(true)}
                      className="w-full bg-green-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-green-700 transition-colors"
                    >
                      <DollarSign className="inline-block h-5 w-5 mr-2" />
                      Exchange for Cash
                    </button>
                  </div>
                ) : exchangeInfo.canExchange ? (
                  <div className="bg-yellow-50 rounded-lg p-6 border border-yellow-200">
                    <h3 className="text-xl font-bold text-yellow-900">Exchange for Cash</h3>
                    <p className="text-yellow-700">
                      Need {exchangeInfo.minimumExchange} points minimum. You have {exchangeInfo.currentBalance} points.
                    </p>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <h3 className="text-xl font-bold text-gray-900">Cash Exchange Unavailable</h3>
                    <p className="text-gray-700">
                      Only referral users can exchange for cash.
                    </p>
                  </div>
                )}

                {/* Voucher Exchange */}
                {exchangeInfo.currentBalance >= 50 ? (
                  <div className="bg-purple-50 rounded-lg p-6 border border-purple-200">
                    <div className="mb-4">
                      <h3 className="text-xl font-bold text-purple-900">Exchange for Vouchers</h3>
                      <p className="text-purple-700">
                        Get discount vouchers for services! Minimum: 50 points
                      </p>
                    </div>
                    <button
                      onClick={handleOpenVoucherModal}
                      className="w-full bg-purple-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-purple-700 transition-colors"
                    >
                      <Gift className="inline-block h-5 w-5 mr-2" />
                      Exchange for Voucher
                    </button>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <h3 className="text-xl font-bold text-gray-900">Exchange for Vouchers</h3>
                    <p className="text-gray-700">
                      Need 50 points minimum. You have {exchangeInfo.currentBalance} points.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Points History */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-xl"
            >
              <div className="p-8 border-b border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900">Points History</h3>
              </div>
              <div className="p-8">
                {pointsHistory.length > 0 ? (
                  <div className="space-y-4">
                    {pointsHistory.map((transaction, index) => (
                      <div key={index} className="flex items-center justify-between py-4 border-b border-gray-100 last:border-b-0">
                        <div>
                          <p className="text-lg font-bold text-gray-900">
                            {transaction.points > 0 ? '+' : ''}{transaction.points} points
                          </p>
                          <p className="text-sm text-gray-600">{transaction.description}</p>
                          <p className="text-xs text-gray-500">
                            {transaction.type.replace(/_/g, ' ')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-gray-900">
                            Balance: {transaction.newBalance} pts
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(transaction.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No points transactions yet</p>
                )}
              </div>
            </motion.div>
          </div>
        )}

        {/* Wallet Tab */}
        {activeTab === 'wallet' && walletData && (
          <div className="space-y-8">
            {/* Wallet Overview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-xl p-8"
            >
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl font-bold text-gray-900">Wallet Overview</h2>
                <Wallet className="h-10 w-10 text-green-600" />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                <div className="bg-green-50 rounded-lg p-6 border-l-4 border-green-600">
                  <p className="text-sm font-bold text-green-600">Total Earnings</p>
                  <p className="text-3xl font-bold text-gray-900">${walletData.wallet.totalEarnings.toFixed(2)}</p>
                </div>

                <div className="bg-purple-50 rounded-lg p-6 border-l-4 border-purple-600">
                  <p className="text-sm font-bold text-purple-600">Wallet Balance</p>
                  <p className="text-3xl font-bold text-gray-900">${(walletData.wallet.walletBalance || 0).toFixed(2)}</p>
                  <p className="text-xs text-purple-600 mt-1">From points exchange</p>
                </div>

                <div className="bg-yellow-50 rounded-lg p-6 border-l-4 border-yellow-600">
                  <p className="text-sm font-bold text-yellow-600">Pending</p>
                  <p className="text-3xl font-bold text-gray-900">${walletData.wallet.pendingEarnings.toFixed(2)}</p>
                </div>

                <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-blue-600">
                  <p className="text-sm font-bold text-blue-600">Available for Payout</p>
                  <p className="text-3xl font-bold text-gray-900">${walletData.wallet.availableForPayout.toFixed(2)}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-gray-600">
                  <p className="text-sm font-bold text-gray-600">Total Paid</p>
                  <p className="text-3xl font-bold text-gray-900">${walletData.wallet.totalPaid.toFixed(2)}</p>
                </div>
              </div>

              {walletData.wallet.availableForPayout >= walletData.minimumPayout && (
                <div className="mt-8 text-center">
                  <button
                    onClick={() => setShowPayoutModal(true)}
                    className="bg-green-600 text-white px-8 py-3 rounded-lg font-bold text-lg hover:bg-green-700 transition-colors"
                  >
                    <CreditCard className="inline-block h-5 w-5 mr-2" />
                    Request Payout
                  </button>
                </div>
              )}
            </motion.div>

            {/* Commission Transactions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-xl"
            >
              <div className="p-8 border-b border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900">Commission Transactions</h3>
              </div>
              <div className="p-8">
                {walletData.recentTransactions?.filter(transaction => transaction.referredUser && transaction.referredUser.firstName).length > 0 ? (
                  <div className="space-y-4">
                    {walletData.recentTransactions.filter(transaction => transaction.referredUser && transaction.referredUser.firstName).map((transaction, index) => (
                      <div key={index} className="flex items-center justify-between py-4 border-b border-gray-100 last:border-b-0">
                        <div>
                          <p className="text-lg font-bold text-gray-900">
                            ${transaction.commissionAmount.toFixed(2)}
                          </p>
                          <p className="text-sm text-gray-600">
                            From {transaction.referredUser?.firstName} {transaction.referredUser?.lastName}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${getStatusColor(transaction.status)}`}>
                            {transaction.status}
                          </span>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(transaction.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No commission transactions yet</p>
                )}
              </div>
            </motion.div>

            {/* Wallet Transactions (Points Exchanges) */}
            {walletData.walletTransactions && walletData.walletTransactions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-xl shadow-xl"
              >
                <div className="p-8 border-b border-gray-200">
                  <h3 className="text-2xl font-bold text-gray-900">Wallet Transactions</h3>
                  <p className="text-sm text-gray-600">From points exchanges</p>
                </div>
                <div className="p-8">
                  <div className="space-y-4">
                    {walletData.walletTransactions.map((transaction, index) => (
                      <div key={index} className="flex items-center justify-between py-4 border-b border-gray-100 last:border-b-0">
                        <div>
                          <p className="text-lg font-bold text-gray-900">
                            {transaction.type === 'CREDIT' ? '+' : '-'}${transaction.amount.toFixed(2)}
                          </p>
                          <p className="text-sm text-gray-600">
                            {transaction.description}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${
                            transaction.type === 'CREDIT' ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'
                          }`}>
                            {transaction.type}
                          </span>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(transaction.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        )}

        {/* Commissions Tab */}
        {activeTab === 'commissions' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-xl"
          >
            <div className="p-8 border-b border-gray-200">
              <h2 className="text-3xl font-bold text-gray-900">Commission History</h2>
            </div>
            <div className="p-8">
              {commissionHistory.filter(commission => commission.referredUser && commission.referredUser.firstName).length > 0 ? (
                <div className="space-y-4">
                  {commissionHistory.filter(commission => commission.referredUser && commission.referredUser.firstName).map((commission, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xl font-bold text-gray-900">
                            ${commission.commissionAmount.toFixed(2)}
                          </p>
                          <p className="text-sm text-gray-600">
                            Commission from {commission.referredUser?.firstName || 'Unknown'} {commission.referredUser?.lastName || 'User'}
                          </p>
                          <p className="text-sm text-gray-500">
                            Order Value: ${commission.orderAmount.toFixed(2)} • Rate: {commission.commissionRate}%
                          </p>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex px-3 py-1 text-sm font-bold rounded-full ${getStatusColor(commission.status)}`}>
                            {commission.status}
                          </span>
                          <p className="text-sm text-gray-500 mt-2">
                            {new Date(commission.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No commissions yet</p>
              )}
            </div>
          </motion.div>
        )}

        {/* Links Tab */}
        {activeTab === 'links' && (
          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-xl p-8"
            >
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl font-bold text-gray-900">Referral Links</h2>
                <button
                  onClick={() => setShowLinkModal(true)}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors"
                >
                  <Plus className="inline-block h-5 w-5 mr-2" />
                  Create Advanced Link
                </button>
              </div>
              
              {referralLinks.length > 0 ? (
                <div className="space-y-4">
                  {referralLinks.map((link, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-lg font-bold text-gray-900">{link.campaign?.name || 'Default Campaign'}</p>
                          <p className="text-sm text-gray-600 font-mono">{link.shortUrl}</p>
                          <p className="text-sm text-gray-500">
                            Clicks: {link.totalClicks} • Created: {new Date(link.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => copyToClipboard(link.shortUrl)}
                            className="p-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                          <button className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors">
                            <Eye className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No custom links created yet</p>
              )}
            </motion.div>
          </div>
        )}

        {/* Alerts Tab */}
        {activeTab === 'alerts' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-xl"
          >
            <div className="p-8 border-b border-gray-200">
              <div className="flex items-center">
                <Shield className="h-8 w-8 text-red-600 mr-3" />
                <h2 className="text-3xl font-bold text-gray-900">Security Alerts</h2>
              </div>
            </div>
            <div className="p-8">
              {fraudAlerts.length > 0 ? (
                <div className="space-y-4">
                  {fraudAlerts.map((alert, index) => (
                    <div key={index} className={`border-l-4 p-4 rounded-lg ${
                      alert.severity === 'CRITICAL' ? 'border-red-600 bg-red-50' :
                      alert.severity === 'HIGH' ? 'border-orange-600 bg-orange-50' :
                      'border-yellow-600 bg-yellow-50'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-lg">{alert.type.replace(/_/g, ' ')}</p>
                          <p className="text-sm text-gray-600">{alert.description}</p>
                          <p className="text-xs text-gray-500 mt-2">
                            Risk Score: {alert.riskScore}% • {new Date(alert.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <span className={`px-3 py-1 text-xs font-bold rounded-full ${
                          alert.severity === 'CRITICAL' ? 'bg-red-100 text-red-600' :
                          alert.severity === 'HIGH' ? 'bg-orange-100 text-orange-600' :
                          'bg-yellow-100 text-yellow-600'
                        }`}>
                          {alert.severity}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
                  <p className="text-lg font-bold text-gray-900">All Clear!</p>
                  <p className="text-gray-500">No security alerts for your referrals</p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Payout Modal */}
        {showPayoutModal && <PayoutModal onClose={() => setShowPayoutModal(false)} onSubmit={handlePayoutRequest} walletData={walletData} />}

        {/* Advanced Link Modal */}
        {showLinkModal && <AdvancedLinkModal onClose={() => setShowLinkModal(false)} onSubmit={handleCreateAdvancedLink} />}

        {/* Exchange Points Modal */}
        {showExchangeModal && exchangeInfo && <ExchangePointsModal onClose={() => setShowExchangeModal(false)} onSubmit={handleExchangePoints} exchangeInfo={exchangeInfo} />}

        {/* Voucher Exchange Modal */}
        {showVoucherModal && <VoucherExchangeModal onClose={() => setShowVoucherModal(false)} onSubmit={handleRedeemVoucher} voucherTemplates={voucherTemplates} userPoints={exchangeInfo?.currentBalance || 0} />}
      </div>
    </div>
  );
};

// Payout Request Modal Component
const PayoutModal = ({ onClose, onSubmit, walletData }) => {
  const [formData, setFormData] = useState({
    paymentMethod: 'BANK_TRANSFER',
    bankDetails: {
      accountNumber: '',
      routingNumber: '',
      accountHolderName: ''
    },
    paypalEmail: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900">Request Payout</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="mb-6 p-4 bg-green-50 rounded-lg">
          <p className="text-sm text-green-600 font-bold">Available for Payout</p>
          <p className="text-2xl font-bold text-green-900">
            ${walletData?.wallet?.availableForPayout?.toFixed(2) || '0.00'}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Payment Method
            </label>
            <select
              value={formData.paymentMethod}
              onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg"
            >
              <option value="BANK_TRANSFER">Bank Transfer</option>
              <option value="PAYPAL">PayPal</option>
            </select>
          </div>

          {formData.paymentMethod === 'BANK_TRANSFER' && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Account Holder Name
                </label>
                <input
                  type="text"
                  value={formData.bankDetails.accountHolderName}
                  onChange={(e) => setFormData({
                    ...formData,
                    bankDetails: { ...formData.bankDetails, accountHolderName: e.target.value }
                  })}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Account Number
                </label>
                <input
                  type="text"
                  value={formData.bankDetails.accountNumber}
                  onChange={(e) => setFormData({
                    ...formData,
                    bankDetails: { ...formData.bankDetails, accountNumber: e.target.value }
                  })}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Routing Number
                </label>
                <input
                  type="text"
                  value={formData.bankDetails.routingNumber}
                  onChange={(e) => setFormData({
                    ...formData,
                    bankDetails: { ...formData.bankDetails, routingNumber: e.target.value }
                  })}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  required
                />
              </div>
            </>
          )}

          {formData.paymentMethod === 'PAYPAL' && (
            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                PayPal Email
              </label>
              <input
                type="email"
                value={formData.paypalEmail}
                onChange={(e) => setFormData({ ...formData, paypalEmail: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg"
                required
              />
            </div>
          )}

          <div className="flex space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-bold hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700"
            >
              Submit Request
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Exchange Points Modal Component
const ExchangePointsModal = ({ onClose, onSubmit, exchangeInfo }) => {
  const [pointsToExchange, setPointsToExchange] = useState(exchangeInfo.minimumExchange || 10);
  const customInputRef = React.useRef(null);

  // Smart preset amounts based on balance - capped at 500 max
  const generatePresetAmounts = () => {
    const min = exchangeInfo.minimumExchange || 10;
    const max = exchangeInfo.currentBalance;
    const MAX_PRESET = 500; // Maximum preset amount
    const presets = new Set();

    // Define sensible round number presets up to 500
    const roundPresets = [10, 20, 50, 100, 200, 500];

    // Add presets that are within user's balance and between min and MAX_PRESET
    roundPresets.forEach(amount => {
      if (amount >= min && amount <= max && amount <= MAX_PRESET) {
        presets.add(amount);
      }
    });

    // Always show at least the minimum if user has enough balance
    if (max >= min && presets.size === 0) {
      presets.add(min);
    }

    return Array.from(presets).sort((a, b) => a - b).slice(0, 6);
  };

  const presetAmounts = generatePresetAmounts();

  const estimatedMoney = pointsToExchange * exchangeInfo.exchangeRate; // Changed from division to multiplication

  const addAmount = (amount) => {
    const newAmount = Math.min(pointsToExchange + amount, exchangeInfo.currentBalance);
    setPointsToExchange(newAmount);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (pointsToExchange < exchangeInfo.minimumExchange) {
      alert(`Minimum exchange amount is ${exchangeInfo.minimumExchange} points`);
      return;
    }
    if (pointsToExchange > exchangeInfo.currentBalance) {
      alert('Insufficient points balance');
      return;
    }
    onSubmit(pointsToExchange);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900">Exchange Points for Cash</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="mb-6 p-4 bg-yellow-50 rounded-lg">
          <p className="text-sm text-yellow-600 font-bold">Available Balance</p>
          <p className="text-2xl font-bold text-yellow-900">{exchangeInfo.currentBalance} points</p>
          <p className="text-sm text-yellow-600">Exchange Rate: 1 point = SGD 1</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Quick Select
            </label>

            <div className="grid grid-cols-3 gap-2 mb-4">
              {presetAmounts.map((amount) => (
                <button
                  key={amount}
                  type="button"
                  onClick={() => setPointsToExchange(amount)}
                  className={`p-3 rounded-lg border font-bold text-sm ${
                    pointsToExchange === amount
                      ? 'bg-green-600 text-white border-green-600'
                      : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  {amount} pts
                </button>
              ))}
            </div>

            <label className="block text-sm font-bold text-gray-700 mb-2">
              Or Enter Custom Amount
            </label>

            <div className="flex items-center gap-2 mb-3">
              <input
                ref={customInputRef}
                type="number"
                value={pointsToExchange}
                onChange={(e) => setPointsToExchange(parseInt(e.target.value) || exchangeInfo.minimumExchange)}
                onFocus={(e) => e.target.select()}
                min={exchangeInfo.minimumExchange}
                max={exchangeInfo.currentBalance}
                className="flex-1 p-3 border-2 border-gray-300 rounded-lg text-center text-lg font-bold focus:border-green-500 focus:ring-2 focus:ring-green-200"
                placeholder={`Min: ${exchangeInfo.minimumExchange}`}
                required
                autoFocus
              />
              <span className="text-gray-600 font-bold">pts</span>
            </div>

            {/* Quick add buttons */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => addAmount(1)}
                className="flex-1 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg font-bold text-sm hover:bg-blue-100 border border-blue-200"
              >
                +1
              </button>
              <button
                type="button"
                onClick={() => addAmount(5)}
                className="flex-1 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg font-bold text-sm hover:bg-blue-100 border border-blue-200"
              >
                +5
              </button>
              <button
                type="button"
                onClick={() => addAmount(10)}
                className="flex-1 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg font-bold text-sm hover:bg-blue-100 border border-blue-200"
              >
                +10
              </button>
              <button
                type="button"
                onClick={() => setPointsToExchange(exchangeInfo.currentBalance)}
                className="flex-1 px-3 py-2 bg-purple-50 text-purple-700 rounded-lg font-bold text-sm hover:bg-purple-100 border border-purple-200"
              >
                MAX
              </button>
            </div>
          </div>

          <div className="mb-6 p-4 bg-green-50 rounded-lg">
            <p className="text-sm text-green-600 font-bold">You will receive</p>
            <p className="text-2xl font-bold text-green-900">${estimatedMoney.toFixed(2)}</p>
            <p className="text-sm text-green-600">
              Points will be deducted from your balance immediately
            </p>
          </div>

          <div className="flex space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-bold hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700"
            >
              Exchange Points
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Advanced Link Creation Modal Component
const AdvancedLinkModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    campaign: '',
    source: 'direct',
    medium: 'referral',
    content: '',
    term: '',
    expiresAt: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900">Create Advanced Link</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Campaign Name
            </label>
            <input
              type="text"
              value={formData.campaign}
              onChange={(e) => setFormData({ ...formData, campaign: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg"
              placeholder="e.g. Facebook Ads"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Source
            </label>
            <input
              type="text"
              value={formData.source}
              onChange={(e) => setFormData({ ...formData, source: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg"
              placeholder="e.g. facebook, twitter, email"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Medium
            </label>
            <input
              type="text"
              value={formData.medium}
              onChange={(e) => setFormData({ ...formData, medium: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg"
              placeholder="e.g. social, email, cpc"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Content (Optional)
            </label>
            <input
              type="text"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg"
              placeholder="Ad content or version"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Expiration Date (Optional)
            </label>
            <input
              type="date"
              value={formData.expiresAt}
              onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg"
            />
          </div>

          <div className="flex space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-bold hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700"
            >
              Create Link
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Voucher Exchange Modal Component
const VoucherExchangeModal = ({ onClose, onSubmit, voucherTemplates, userPoints }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedTemplate) {
      alert('Please select a voucher template');
      return;
    }
    onSubmit(selectedTemplate.id);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">Exchange Points for Voucher</h3>
            <p className="text-gray-600">Available Points: <strong>{userPoints}</strong></p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {voucherTemplates.map((template) => (
              <div
                key={template.id}
                onClick={() => template.canAfford && setSelectedTemplate(template)}
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  selectedTemplate?.id === template.id
                    ? 'border-purple-600 bg-purple-50'
                    : template.canAfford
                    ? 'border-gray-200 hover:border-purple-300'
                    : 'border-gray-200 opacity-50 cursor-not-allowed'
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-bold text-lg">{template.name}</h4>
                  <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                    template.canAfford ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {template.pointsCost} pts
                  </span>
                </div>
                <p className="text-gray-600 text-sm mb-3">{template.description}</p>
                <div className="space-y-1 text-xs text-gray-500">
                  <p>Min. Order: SGD {template.minimumOrderAmount}</p>
                  <p>Valid for: {template.validForDays} days</p>
                  {template.category !== 'ALL' && <p>Category: {template.category}</p>}
                  {template.maximumDiscountAmount && <p>Max Discount: SGD {template.maximumDiscountAmount}</p>}
                  {!template.canAfford && (
                    <p className="text-red-500 font-bold mt-2">Need {template.pointsNeeded} more points</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {voucherTemplates.length === 0 && (
            <p className="text-gray-500 text-center py-8">Loading voucher templates...</p>
          )}

          {selectedTemplate && (
            <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-600 font-bold">Selected Voucher</p>
              <p className="text-lg font-bold text-purple-900">{selectedTemplate.name}</p>
              <p className="text-sm text-purple-700">
                This will cost {selectedTemplate.pointsCost} points. Remaining balance: {userPoints - selectedTemplate.pointsCost} points
              </p>
            </div>
          )}

          <div className="flex space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-bold hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedTemplate}
              className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Exchange for Voucher
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default ReferralDashboardPage;